import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

// Shea's actual photos
const SHEA_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_ai-leadership-5/artifacts/ao5ky1z5_IMG_7418.jpeg";

export default function HeroSection() {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background with gradient */}
      <div className="absolute inset-0 z-0 hero-gradient" />
      
      {/* Animated gradient blobs */}
      <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] gradient-blob animate-pulse" />
      <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] gradient-blob animate-pulse" style={{ animationDelay: '1s' }} />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 mb-8"
            >
              <Sparkles size={16} className="text-primary" />
              <span className="text-xs font-['Orbitron'] uppercase tracking-[0.15em] text-primary">
                AI Survival Blueprint Available
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-['Orbitron'] text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6 leading-tight"
            >
              Reinventing
              <br />
              <span className="gradient-text">Leadership</span>
              <br />
              <span className="text-muted-foreground text-3xl sm:text-4xl lg:text-5xl">in the Age of AI</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-base sm:text-lg text-muted-foreground max-w-xl mb-10 leading-relaxed"
            >
              Serial entrepreneur. EdTech innovator. Thought leader guiding professionals 
              and organizations to adapt, lead, and thrive through the AI revolution.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-start gap-4"
            >
              <Button
                onClick={() => scrollToSection("#lead-magnet")}
                className="bg-primary text-primary-foreground rounded-full px-8 py-6 font-bold uppercase tracking-wider text-sm hover:bg-primary/90 transition-all btn-glow group"
                data-testid="hero-cta-guide"
              >
                Explore More
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button
                onClick={() => scrollToSection("#speaking-form")}
                variant="outline"
                className="bg-transparent border-2 border-primary/50 text-primary rounded-full px-8 py-6 font-bold uppercase tracking-wider text-sm hover:bg-primary/10 hover:border-primary transition-all group"
                data-testid="hero-cta-speaking"
              >
                <Calendar size={18} className="mr-2" />
                Book Shea
              </Button>
            </motion.div>

            {/* Page indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="flex items-center gap-4 mt-16"
            >
              <span className="font-['Orbitron'] text-4xl font-bold text-primary">01</span>
              <span className="text-muted-foreground">/</span>
              <span className="font-['Orbitron'] text-lg text-muted-foreground">04</span>
              <div className="flex gap-2 ml-4">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
              </div>
            </motion.div>
          </div>

          {/* Right Content - Shea's Photo */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="hidden lg:flex justify-center items-center relative"
          >
            <div className="relative">
              {/* Glowing frame */}
              <div className="absolute -inset-4 rounded-2xl border border-primary/30" />
              <div className="absolute -inset-8 rounded-2xl border border-primary/10" />
              
              {/* Image container */}
              <div className="relative frame-border p-2">
                <img
                  src={SHEA_HERO_IMAGE}
                  alt="Shea Richburg"
                  className="w-full max-w-md aspect-[3/4] object-cover object-top rounded-xl"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-accent/10 rounded-xl" />
              </div>

              {/* Floating elements */}
              <div className="absolute -right-8 top-1/4 w-16 h-16 rounded-full border border-primary/50 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full border border-primary animate-pulse" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px glow-line" />
    </section>
  );
}
